import * as React from "react";
import {
  Box,
  Input,
  Button,
  Text,
  VStack,
  HStack,
  InputGroup,
  InputLeftElement,
  Flex,
  Stack,
  Icon,
} from "@chakra-ui/react";
import { ActionButton } from "./ActionButton";
import { SuggestedPrompt } from "./SuggestedPrompt";
import { calculateTotalFees, getLoanStatistics } from "./mockData";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import WidgetBox from "../WidgetBox/widget-box";
import { useFeatures } from "hooks/useFeatures";

export const AskLoans: React.FC = () => {
  const { hasFeature } = useFeatures();
  const [promptValue, setPromptValue] = React.useState("");
  const [response, setResponse] = React.useState<string | null>(null);

  // If not the specific company, don't render anything
  if (!hasFeature("ai")) {
    return null;
  }

  const actionButtons = [
    { icon: "file-text", text: "Summarize" },
    { icon: "chart-bar", text: "Report" },
    { icon: "pencil", text: "Help Me Write" },
  ];

  const suggestedPrompts = [
    "Generate a report of our revenue from fees from the last 6 months",
    "What tasks should I complete today?",
  ];

  const generateResponse = (prompt: string) => {
    // Simple response generation based on prompt keywords
    if (prompt.toLowerCase().includes("revenue") && prompt.toLowerCase().includes("fees")) {
      const sixMonthsAgo = new Date();
      sixMonthsAgo.setMonth(sixMonthsAgo.getMonth() - 6);
      const totalFees = calculateTotalFees(sixMonthsAgo.toISOString(), new Date().toISOString());
      return `Total revenue from fees in the last 6 months: $${totalFees.toLocaleString()}`;
    }

    if (prompt.toLowerCase().includes("tasks")) {
      const stats = getLoanStatistics();
      return `Here are your tasks for today:
1. Review ${stats.totalActiveLoans} active loans
2. Process pending payments
3. Check loans with upcoming maturity dates
4. Review any late payments`;
    }

    if (prompt.toLowerCase().includes("summary") || prompt.toLowerCase().includes("overview")) {
      const stats = getLoanStatistics();
      return `Loan Portfolio Summary:
• Total Active Loans: ${stats.totalActiveLoans}
• Total Loan Amount: $${stats.totalLoanAmount.toLocaleString()}
• Average Interest Rate: ${stats.averageInterestRate.toFixed(2)}%`;
    }

    return "I apologize, but I don't have enough information to answer that question. Please try rephrasing or ask another question.";
  };

  const handleSubmit = () => {
    if (promptValue.trim()) {
      const answer = generateResponse(promptValue);
      setResponse(answer);
    }
  };

  const handleActionClick = (action: string) => {
    let prompt = "";
    switch (action) {
      case "Summarize":
        prompt = "Give me a summary of the current loan portfolio";
        break;
      case "Report":
        prompt = "Generate a report of our revenue from fees from the last 6 months";
        break;
      case "Help Me Write":
        prompt = "What tasks should I complete today?";
        break;
    }
    setPromptValue(prompt);
    const answer = generateResponse(prompt);
    setResponse(answer);
  };

  const handleSuggestedPromptClick = (prompt: string) => {
    setPromptValue(prompt);
    const answer = generateResponse(prompt);
    setResponse(answer);
  };

  return (
    <WidgetBox>
      <Stack gap={4}>
        <Text fontSize="lg" fontWeight="semibold">
          Ask me anything about your loans
        </Text>

        <Flex gap={2}>
          <InputGroup flex={1}>
            <InputLeftElement>
              <Icon as={FontAwesomeIcon} icon={["far", "sparkles"]} color="gray.600" />
            </InputLeftElement>
            <Input
              value={promptValue}
              variant="primary"
              onChange={e => setPromptValue(e.target.value)}
              placeholder="Enter a prompt here or select a suggested prompt..."
              onKeyDown={e => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  handleSubmit();
                }
              }}
            />
          </InputGroup>
          <Button onClick={handleSubmit} colorScheme="gray">
            Generate
          </Button>
        </Flex>

        <HStack spacing={2}>
          {actionButtons.map(button => (
            <ActionButton
              key={button.text}
              {...button}
              onClick={() => handleActionClick(button.text)}
            />
          ))}
        </HStack>

        <VStack align="stretch" spacing={2}>
          <Text fontSize="sm" fontWeight="medium">
            Suggested Prompts
          </Text>
          <Flex wrap="wrap" gap={2}>
            {suggestedPrompts.map(prompt => (
              <SuggestedPrompt
                key={prompt}
                text={prompt}
                onClick={() => handleSuggestedPromptClick(prompt)}
              />
            ))}
          </Flex>
        </VStack>

        {response && (
          <Box mt={6} p={4} bg="gray.50" borderRadius="md">
            <Text whiteSpace="pre-line">{response}</Text>
          </Box>
        )}
      </Stack>
    </WidgetBox>
  );
};
